import { useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getCockpitMetrics } from '@/api/clarity'
import { formatMetric } from '@/hooks/clarity/util'
import { CockpitMetricsModel, MetricCardComparisonModel } from '@/types/clarity'
import { dateToString } from '@/utils'
import { DateType, getDateRange, getTrendingDate } from '@/utils/date'

const COMPANY_SYSTEM = 'Company System'

export default function useCockpitMetrics(
  startDate: string,
  endDate: string,
  dateFilterType: DateType,
  branches: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [branchData, setBranchData] = useState<CockpitMetricsModel[]>([])
  const [branchTrendData, setBranchTrendData] = useState<CockpitMetricsModel[]>(
    []
  )
  const [companyData, setCompanyData] = useState<CockpitMetricsModel>()
  const [companyTrendData, setCompanyTrendData] =
    useState<CockpitMetricsModel>()
  const [metricCardData, setMetricCardData] =
    useState<MetricCardComparisonModel<CockpitMetricsModel>>()

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { branchMetrics, companyMetrics } = await fetchCockpitMetrics(
          branches,
          startDate,
          endDate
        )

        setLocalTrendingData(branches, setMetricCardData)

        setBranchData(branchMetrics)
        setCompanyData(companyMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange

        const { branchMetrics, companyMetrics } = await fetchCockpitMetrics(
          branches,
          dateToString(trendingDate, 'from'),
          dateToString(trendingDate, 'to')
        )

        setBranchTrendData(branchMetrics)
        setCompanyTrendData(companyMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate, dateFilterType])

  useEffect(() => {
    setLoading(true)

    const fetchAllData = async () => {
      try {
        // Execute all fetch operations in parallel
        await Promise.all([fetchData(), fetchTrendData()])
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      } finally {
        // Set loading to false only after all operations are complete
        setLoading(false)
      }
    }

    fetchAllData()
  }, [fetchData, fetchTrendData])

  return {
    branchData,
    companyData,
    branchTrendData,
    companyTrendData,
    metricCardData,
    loading,
    error,
  }
}

async function setLocalTrendingData(
  branches: string,
  setMetricCardData: (
    data: MetricCardComparisonModel<CockpitMetricsModel>
  ) => void
) {
  const mtdDate = getDateRange('month-to-date')
  const ytdDate = getDateRange('year-to-date')
  const mtdTrendingDate = mtdDate.trending as DateRange
  const ytdTrendingDate = ytdDate.trending as DateRange

  const { companyMetrics: mCompanyMetrics } = await fetchCockpitMetrics(
    branches,
    dateToString(mtdDate, 'from'),
    dateToString(mtdDate, 'to')
  )

  const { companyMetrics: mCompanyMetricsTrending } = await fetchCockpitMetrics(
    branches,
    dateToString(mtdTrendingDate, 'from'),
    dateToString(mtdTrendingDate, 'to')
  )

  const { companyMetrics: yCompanyMetrics } = await fetchCockpitMetrics(
    branches,
    dateToString(ytdDate, 'from'),
    dateToString(ytdDate, 'to')
  )

  const { companyMetrics: yCompanyMetricsTrending } = await fetchCockpitMetrics(
    branches,
    dateToString(ytdTrendingDate, 'from'),
    dateToString(ytdTrendingDate, 'to')
  )

  setMetricCardData({
    currMetricsMonthly: mCompanyMetrics,
    currMetricsYearly: yCompanyMetrics,
    prevMetricsMonthly: mCompanyMetricsTrending,
    prevMetricsYearly: yCompanyMetricsTrending,
  })
}

async function fetchCockpitMetrics(
  branches: string,
  startDate: string,
  endDate: string
): Promise<{
  branchMetrics: CockpitMetricsModel[]
  companyMetrics: CockpitMetricsModel
}> {
  try {
    const response = await getCockpitMetrics(branches, startDate, endDate)

    const metrics: CockpitMetricsModel[] = response.map((responseMetrics) =>
      setMetric(
        responseMetrics.branch_name,
        responseMetrics.adns,
        responseMetrics.sss,
        responseMetrics.average_check,
        responseMetrics.traffic,
        responseMetrics.sst,
        responseMetrics.total_discounts,
        responseMetrics.total_discounts_ratio,
        responseMetrics.labor_ratio,
        responseMetrics.labor_hours,
        responseMetrics.labor_cost,
        responseMetrics.total_net_sales,
        responseMetrics.total_total_traffic,
        responseMetrics.cogs_pti,
        responseMetrics.cogs_mix,
        responseMetrics.cogs_waste,
        responseMetrics.speed_of_service,
        responseMetrics.guest_satisfaction,
        responseMetrics.total_previous_year_net_sales,
        responseMetrics.total_previous_year_total_traffic
      )
    )

    const branchMetrics = metrics.filter(
      (metric) => metric.stand !== COMPANY_SYSTEM
    )
    const companyMetrics = metrics.filter(
      (metric) => metric.stand === COMPANY_SYSTEM
    )

    return {
      branchMetrics,
      companyMetrics: companyMetrics[0],
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function setMetric(
  stand: string,
  adns: number,
  sss: number,
  avgCheck: number,
  traffic: number,
  sst: number,
  totalDiscounts: number,
  totalDiscountsRatio: number,
  laborRatio: number,
  laborHours: number,
  laborCost: number,
  totalNetSales: number,
  totalTraffic: number,
  cogsPti: number,
  cogsMix: number,
  cogsWaste: number,
  speedOfService: number,
  guestSatisfaction: number,
  prevYearTotalSales: number,
  prevYearTotalTraffic: number
): CockpitMetricsModel {
  return {
    stand: stand === 'system_total' ? COMPANY_SYSTEM : stand,
    adns: formatMetric(adns, 0),
    sss: formatMetric(sss, 1),
    avgCheck: formatMetric(avgCheck, 2),
    traffic: formatMetric(traffic, 0),
    sst: formatMetric(sst, 2),
    totalDiscounts: formatMetric(totalDiscounts, 1),
    totalDiscountsRatio: formatMetric(totalDiscountsRatio, 1),
    laborRatio: formatMetric(laborRatio, 1),
    laborHours: formatMetric(laborHours, 0),
    laborCost: formatMetric(laborCost, 0),
    netSales: formatMetric(totalNetSales, 0),
    totalTraffic: formatMetric(totalTraffic, 0),
    cogsPti: formatMetric(cogsPti, 0),
    cogsMix: formatMetric(cogsMix, 0),
    cogsWaste: formatMetric(cogsWaste, 0),
    speedOfService: formatMetric(speedOfService, 0),
    guestSatisfaction: formatMetric(guestSatisfaction, 0),
    prevYearTotalSales: formatMetric(prevYearTotalSales, 0),
    prevYearTotalTraffic: formatMetric(prevYearTotalTraffic, 0),
  }
}
